/**
 * Setup Header Component
 * 
 * Header component for account setup and password reset pages.
 * Displays logo, title, and personalized greeting.
 * 
 * @module pages/auth/components
 */

import { TenantLogo } from '@butter/ui/components/data-display';
import { stripPlaceholderName } from '@butter/library/shared';

export interface SetupHeaderProps {
  /** Page title */
  title: string;
  /** User's first name for personalized greeting */
  firstName?: string;
  /** Subtitle or description text */
  subtitle?: string;
  /** Tenant handle for displaying the correct logo */
  tenantHandle?: string | null;
}

/**
 * Setup Header
 * 
 * Displays:
 * - Tenant logo (falls back to Butter logo)
 * - Page title
 * - Personalized greeting (if firstName provided)
 * - Subtitle text
 * 
 * @example
 * ```tsx
 * <SetupHeader 
 *   title="Account Setup"
 *   firstName="John"
 *   subtitle="Complete your profile to get started."
 * />
 * ```
 */
export function SetupHeader({ 
  title, 
  firstName, 
  subtitle,
  tenantHandle
}: SetupHeaderProps) {
  // Imported members with no name on file carry the `[unknown]` placeholder — greet them generically.
  const name = stripPlaceholderName(firstName);
  const greeting = name ? `Hello ${name}!` : 'Hello there!';

  return (
    <header className="text-center mb-8" role="banner">
      {/* Logo — TenantLogo self-sizes based on logoSize in the registry */}
      <div className="flex justify-center mb-6">
        <TenantLogo tenantHandle={tenantHandle || undefined} />
      </div>
      
      {/* Title */}
      <h1 className="text-2xl font-bold text-fg-primary mb-2">
        {title}
      </h1>
      
      {/* Greeting */}
      <p className="text-lg text-fg-quaternary">
        {greeting}
      </p>
      
      {/* Subtitle */}
      {subtitle && (
        <p className="text-fg-quaternary mt-1">
          {subtitle}
        </p>
      )}
    </header>
  );
}
